<template>
  <div>
    <div class="d-flex justify-content-between align-items-center flex-wrap mb-1">
      <div>
        <h4 class="mb-0">{{ client.title || 'Partner API client' }}</h4>
        <small class="text-muted">
          <code v-if="client.client_id">{{ client.client_id }}</code>
          <span v-if="client.client_id"> · </span>{{ windowSummary }}
        </small>
      </div>

      <div class="d-flex align-items-center">
        <usage-window-picker
          v-model="preset"
          :disabled="loading"
          class="mr-1"
          @refresh="fetchUsage"
        />
        <b-button
          v-if="can('api_clients:CanView')"
          size="sm"
          variant="outline-secondary"
          :to="{ name: 'api-clients-view', params: { id: recordId } }"
        >
          Configuration
        </b-button>
      </div>
    </div>

    <b-overlay :show="loading" rounded="sm">
      <usage-totals :totals="totals" />

      <usage-timeseries-chart :timeseries="timeseries" />

      <b-row>
        <b-col cols="12" lg="7" class="mb-2 mb-lg-0">
          <usage-routes-table :routes="routes" />
        </b-col>
        <b-col cols="12" lg="5">
          <usage-latency-chart :latency="latency" />
        </b-col>
      </b-row>

      <b-tabs class="mt-2">
        <b-tab title="Failures" active @click="ensureErrors">
          <usage-errors-table :errors="errors" />
        </b-tab>

        <b-tab title="Request log" lazy>
          <usage-logs-table :client-id="recordId" :window="logWindow" />
        </b-tab>
      </b-tabs>
    </b-overlay>
  </div>
</template>

<script>
import { get } from 'lodash'
import {
  BRow,
  BCol,
  BTab,
  BTabs,
  BButton,
  BOverlay,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

import UsageTotals from '@core/components/api-clients/usage/UsageTotals.vue'
import UsageLogsTable from '@core/components/api-clients/usage/UsageLogsTable.vue'
import UsageErrorsTable from '@core/components/api-clients/usage/UsageErrorsTable.vue'
import UsageRoutesTable from '@core/components/api-clients/usage/UsageRoutesTable.vue'
import UsageWindowPicker from '@core/components/api-clients/usage/UsageWindowPicker.vue'
import UsageLatencyChart from '@core/components/api-clients/usage/UsageLatencyChart.vue'
import UsageTimeseriesChart from '@core/components/api-clients/usage/UsageTimeseriesChart.vue'
import {
  windowParams,
  WINDOW_PRESETS,
} from '@core/components/api-clients/usage/usageFormat'

/**
 * One partner's usage in full.
 *
 * The aggregate half (totals, series, endpoints, latency) is one request against
 * pre-aggregated rollups. The two tabs below read individual request rows, which is
 * more expensive and much shorter-lived data — so the log tab is `lazy` and only loads
 * when someone actually opens it.
 */
export default {
  components: {
    BRow,
    BCol,
    BTab,
    BTabs,
    BButton,
    BOverlay,
    UsageTotals,
    UsageLogsTable,
    UsageErrorsTable,
    UsageRoutesTable,
    UsageLatencyChart,
    UsageWindowPicker,
    UsageTimeseriesChart,
  },
  data() {
    return {
      loading: false,
      preset: WINDOW_PRESETS[1],

      client: {},
      totals: {},
      routes: [],
      errors: [],
      timeseries: { granularity: 'hour', points: [] },
      latency: { labels: [], counts: [] },

      resolvedWindow: null,
      errorsLoaded: false,
    }
  },
  computed: {
    recordId() {
      return this.$route.params.id
    },
    /** Passed to the log table, which pages within the same window. */
    logWindow() {
      return windowParams(this.preset)
    },
    windowSummary() {
      if (!this.resolvedWindow) return this.preset.label

      const from = new Date(this.resolvedWindow.from)
      const to = new Date(this.resolvedWindow.to)

      if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return this.preset.label

      return `${from.toLocaleString()} — ${to.toLocaleString()}`
    },
  },
  watch: {
    preset() {
      this.errorsLoaded = false
      this.fetchUsage()
      this.ensureErrors()
    },
  },
  created() {
    this.fetchClient()
    this.fetchUsage()
    this.ensureErrors()
  },
  methods: {
    notifyError(error) {
      const error_message = get(error, 'response.data.message') || error.message
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Error!',
          icon: 'AlertTriangleIcon',
          variant: 'danger',
          text: error_message,
        },
      })
    },
    async fetchClient() {
      try {
        const request = await this.useJwt().mainService.apiClients.get(this.recordId)
        this.client = get(request, 'data.data', {})
      } catch (error) {
        this.notifyError(error)
      }
    },
    async fetchUsage() {
      try {
        this.loading = true

        const request = await this.useJwt().mainService.apiClients.usage(
          this.recordId,
          windowParams(this.preset),
        )
        const payload = get(request, 'data.data', {})

        this.totals = payload.totals || {}
        this.routes = payload.routes || []
        this.timeseries = payload.timeseries || { granularity: 'hour', points: [] }
        this.latency = payload.latency || { labels: [], counts: [] }
        this.resolvedWindow = payload.window || null
      } catch (error) {
        this.notifyError(error)
      } finally {
        this.loading = false
      }
    },
    /** Failures are the default tab, so they load once per window rather than per click. */
    async ensureErrors() {
      if (this.errorsLoaded) return

      try {
        this.errorsLoaded = true

        const params = windowParams(this.preset)
        delete params.granularity

        const request = await this.useJwt().mainService.apiClients.usageErrors(
          this.recordId,
          params,
        )
        this.errors = get(request, 'data.data.errors', [])
      } catch (error) {
        this.errorsLoaded = false
        this.notifyError(error)
      }
    },
  },
}
</script>
